import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { setSchoolLockedHandler } from '@/core/api';
import { usePermission } from '@/core/permissions';

/** Where a non-admin lands when the school's API is closed. */
export const SCHOOL_PAUSED_PATH = '/school-paused';

/**
 * The app half of the global 402 handler (backend ADR-024).
 *
 * `core/api/client.ts` recognises the `school_locked` body on every request and
 * calls one registered handler; this component IS that handler, mounted inside
 * the router so it can navigate and inside the session so it knows the role.
 * The split matters: the client must not import the router or the permission
 * layer, and no feature should be checking for 402 on its own queries.
 *
 * Three rules:
 *  - **Admins go to `/billing`**, which stays reachable while locked (the
 *    middleware exempts `/api/billing/**`) and is where the lock is actually
 *    fixed. Everyone else gets the read-only explanation.
 *  - **Nobody is signed out.** The credentials are valid; it is the SCHOOL that
 *    owes money. Logging out would also strand a user who belongs to another,
 *    paying school — the switcher in the header is their way there.
 *  - **Already on the target ⇒ no navigation.** A locked school fails many
 *    queries at once (nav badges, dashboards, polls), so the handler fires
 *    repeatedly; without this guard the destination would remount on a loop.
 *    The current pathname is a dependency rather than a ref precisely so the
 *    registered closure can never compare against a stale one.
 */
export function SchoolLockHandler() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAdmin } = usePermission();

  useEffect(() => {
    setSchoolLockedHandler(() => {
      const target = isAdmin ? '/billing' : SCHOOL_PAUSED_PATH;
      if (pathname === target) return;
      navigate(target, { replace: true });
    });
    return () => setSchoolLockedHandler(() => {});
  }, [isAdmin, navigate, pathname]);

  return null;
}
